import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Box,
} from "@mui/material";

import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import React from "react";

const DrillingProgressViewModal = ({
  open,
  onClose,
  projectName,
  borehole,
  progressData,
}) => {
  if (!borehole) return null;

  const progress = Array.isArray(progressData) ? progressData : [];

  /* ========================================================= */
  /* CALCULATE SUMMARY */
  /* ========================================================= */

  const rigName = borehole.rig_name || "-";

  // Total meterage from progress records
  const totalMeterage = progress.reduce(
    (total, row) => total + Number(row.meterage || 0),
    0,
  );

  // Number of distinct days on which progress was recorded
  const progressDays = new Set(
    progress.map((row) => dayjs(row.date).format("YYYY-MM-DD")),
  ).size;

  // Average meterage per recorded progress day
  const averagePerDay = progressDays > 0 ? totalMeterage / progressDays : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <DialogTitle
        sx={{
          bgcolor: "#1e1e1e",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "gray",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {projectName}
          </Typography>

          <Typography variant="h6" fontWeight={600}>
            Borehole: {borehole.borehole_code}
          </Typography>

          {/* ================================================= */}
          {/* BOREHOLE SUMMARY TABLE */}
          {/* ================================================= */}

          <Box sx={{ mt: 1 }}>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed",
                  width: "100%",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        py: 0.5,
                        px: 0.8,
                        width: "35%",
                      }}
                    >
                      Rig Name
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        py: 0.5,
                        px: 0.5,
                        width: "18%",
                      }}
                    >
                      No. of Days
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        py: 0.5,
                        px: 0.5,
                        width: "23%",
                      }}
                    >
                      Total Meterage
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        py: 0.5,
                        px: 0.5,
                        width: "24%",
                      }}
                    >
                      Average m/day
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: "0.75rem",
                        py: 0.6,
                        px: 0.8,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={rigName}
                    >
                      {rigName}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "0.75rem",
                        py: 0.6,
                        px: 0.5,
                      }}
                    >
                      {progressDays}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "0.75rem",
                        py: 0.6,
                        px: 0.5,
                      }}
                    >
                      {totalMeterage.toFixed(2)} m
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "0.75rem",
                        py: 0.6,
                        px: 0.5,
                        color: "primary.main",
                        fontWeight: 600,
                      }}
                    >
                      {averagePerDay.toFixed(2)} m/day
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
            ml: 1,
            alignSelf: "flex-start",
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ===================================================== */}
      {/* PROGRESS CONTENT */}
      {/* ===================================================== */}

      <DialogContent sx={{ mt: 2 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#222",
                  }}
                >
                  Date
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#222",
                  }}
                >
                  Meterage (m)
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {progress.length > 0 ? (
                progress.map((row, index) => (
                  <TableRow key={row.id ?? index}>
                    <TableCell>
                      {dayjs(row.date).format("DD-MM-YYYY")}
                    </TableCell>

                    <TableCell>
                      {Number(row.meterage || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No records available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default DrillingProgressViewModal;
